/**
 * Página de Detalle de Receta - Versión Inmersiva (Awwwards Style)
 * Diseño inmersivo con animaciones GSAP para recetas individuales
 * 
 * Según estructura.md - Plantilla de Receta Individual:
 * - Imagen principal y Título
 * - Introducción breve
 * - Ingredientes: Lista clara
 * - Elaboración: Paso a paso numerado
 * - Productos usados: Enlace al producto Intercasa utilizado
 * - Cierre: "Crea, combina y disfruta con Intercasa"
 * 
 * Según correciones.md:
 * - Eliminar "Sobre esta receta"
 */

import React, { useEffect, useRef, useLayoutEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import useFormatDate from '../hooks/useFormatDate';
import { useParams, useLocation } from 'react-router-dom';
import LocalizedNavigate from '../components/common/LocalizedNavigate';
import LocalizedLink from '../components/common/LocalizedLink';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ImmersiveLayout from '../components/layout/ImmersiveLayout';
import { AnimatedSection, MagneticButton } from '../components/animations';
import { getRecipeBySlug, getAllRecipes } from '../data/recipesData';
import { getProductById } from '../data/retailProductsData';
import useLocalizeContent from '../hooks/useLocalizeContent';

gsap.registerPlugin(ScrollTrigger);

/**
 * Obtiene la imagen del producto a partir del link
 * Extrae el productId del link y busca la imagen en retailProductsData
 * @param {string} productLink - Link del producto (ej: '/productos/retail/ketchup-pe')
 * @returns {string|null} URL de la imagen del producto o null
 */
const getProductImage = (productLink) => {
  if (!productLink) return null;
  // Extraer el productId del link (última parte de la URL)
  const parts = productLink.split('/');
  const productId = parts[parts.length - 1];
  const product = getProductById(productId);
  return product?.image || null;
};

/**
 * Card de receta relacionada
 */
const RelatedRecipeCard = ({ title, slug, imageUrl, imageAlt, category, url }) => (
  <LocalizedLink to={url} className="group block">
    <article className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300">
      <div className="relative aspect-[4/3] bg-intercasa-bone overflow-hidden">
        {imageUrl ? (
          <img 
            src={imageUrl} 
            alt={imageAlt || title} 
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-intercasa-bone to-white">
            <svg className="w-12 h-12 text-intercasa-red/20" fill="currentColor" viewBox="0 0 24 24">
              <path d="M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z"/>
            </svg>
          </div>
        )}
        {category && (
          <div className="absolute top-2 left-2 bg-intercasa-red text-white text-xs font-lato font-semibold px-2 py-1 rounded-full">
            {category}
          </div>
        )}
      </div>
      <div className="p-4">
        <h4 className="font-baskerville font-bold text-intercasa-dark group-hover:text-intercasa-red transition-colors">
          {title}
        </h4>
      </div>
    </article>
  </LocalizedLink>
);

/**
 * Página de detalle de receta con diseño inmersivo
 * @returns {JSX.Element} Página de detalle de receta
 */
const RecipeDetailPageImmersive = () => {
  const { t } = useTranslation('recipes');
  const formatDate = useFormatDate();
  const { slug } = useParams();
  const location = useLocation();
  const { localize } = useLocalizeContent();
  // La búsqueda usa el slug (valor simple); después se resuelve el idioma
  const recipe = localize(getRecipeBySlug(slug));
  const heroRef = useRef(null);
  
  // Obtener otras recetas para la sección de relacionados
  const allRecipes = localize(getAllRecipes());
  const relatedRecipes = allRecipes.filter(r => r.slug !== slug).slice(0, 3);

  // Scroll al inicio cuando cambia la receta - useLayoutEffect para ejecutar antes del pintado
  useLayoutEffect(() => {
    // Scroll inmediato antes de que se pinte la página
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Scroll con Lenis si está disponible
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    }
    
    // Reforzar scroll después de que Lenis se inicialice completamente
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
      if (window.__lenis) {
        window.__lenis.scrollTo(0, { immediate: true });
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [slug, location.key]);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero || !recipe) return;

    const tl = gsap.timeline();
    
    tl.fromTo(hero.querySelector('.hero-category'),
      { y: 20, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.5, ease: 'power3.out' }
    )
    .fromTo(hero.querySelector('.hero-title'),
      { y: 40, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.7, ease: 'power3.out' },
      '-=0.3'
    )
    .fromTo(hero.querySelector('.hero-meta'),
      { y: 20, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.5, ease: 'power3.out' },
      '-=0.3'
    );

    return () => tl.kill();
  }, [recipe]);

  // Si no se encuentra la receta, redirigir a la página de recetas
  if (!recipe) {
    return <LocalizedNavigate to="/recetas" replace />;
  }

  return (
    <ImmersiveLayout>
      {/* 
        =============================================
        1. HERO CON IMAGEN DE LA RECETA
        =============================================
      */}
      <section 
        ref={heroRef}
        className="relative min-h-[60vh] md:min-h-[70vh] flex items-end overflow-hidden"
      >
        {/* Imagen de fondo */}
        <div className="absolute inset-0">
          {recipe.imageUrl ? (
            <img 
              src={recipe.imageUrl} 
              alt={recipe.imageAlt || recipe.title} 
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-intercasa-bone to-white flex items-center justify-center">
              <svg className="w-32 h-32 text-intercasa-red/20" fill="currentColor" viewBox="0 0 24 24">
                <path d="M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z"/>
              </svg>
            </div>
          )}
          
          {/* Overlay gradiente */}
          <div className="absolute inset-0 bg-gradient-to-t from-intercasa-dark via-intercasa-dark/50 to-transparent" />
        </div>

        {/* Contenido del hero */}
        <div className="relative z-10 container mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="max-w-4xl">
            {/* Breadcrumb */}
            <div className="flex items-center space-x-2 text-white/60 font-lato text-sm mb-4">
              <LocalizedLink to="/recetas" className="hover:text-white transition-colors">{t('common:nav.recipes')}</LocalizedLink>
              <span>/</span>
              <span className="text-intercasa-yellow">{recipe.title}</span>
            </div>

            {/* Categoría */}
            {recipe.category && (
              <span className="hero-category inline-block bg-intercasa-red text-white text-sm font-lato font-semibold px-4 py-1.5 rounded-full mb-4">
                {recipe.category}
              </span>
            )}

            {/* Título */}
            <h1 className="hero-title font-baskerville font-bold text-white text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-tight mb-4">
              {recipe.title}
            </h1>

            {/* Meta info */}
            <div className="hero-meta flex flex-wrap items-center gap-4 text-white/70 font-lato">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <time dateTime={recipe.date}>{formatDate(recipe.date)}</time>
              </div>
              {recipe.ingredients && (
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <span>{t('detail.ingredientCount', { count: recipe.ingredients.length })}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* 
        =============================================
        2. CONTENIDO DE LA RECETA
        =============================================
      */}
      <section className="bg-intercasa-bone py-12 md:py-20">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            
            {/* Introducción de la receta (según recetas.md: 3-5 líneas explicando la receta) */}
            {(recipe.introduction || recipe.description) && (
              <AnimatedSection animation="fadeUp" delay={0}>
                <p className="font-lato text-lg md:text-xl text-intercasa-dark/80 leading-relaxed mb-12 text-center">
                  {recipe.introduction || recipe.description}
                </p>
              </AnimatedSection>
            )}

            <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
              {/* Columna de ingredientes */}
              <div className="lg:col-span-1">
                <AnimatedSection animation="fadeUp" delay={0.1}>
                  <div className="bg-white rounded-xl p-6 md:p-8 shadow-sm sticky top-24">
                    <h2 className="font-baskerville font-bold text-intercasa-dark text-xl md:text-2xl mb-6 flex items-center">
                      <span className="w-8 h-8 bg-intercasa-red text-white rounded-full flex items-center justify-center mr-3 text-sm">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                      </span>
                      {t('detail.ingredients')}
                    </h2>
                    <ul className="space-y-3">
                      {recipe.ingredients && recipe.ingredients.map((ingredient, index) => (
                        <li key={index} className="flex items-start font-lato text-intercasa-dark/80">
                          <span className="text-intercasa-red mr-3 mt-0.5">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </span>
                          <span>{ingredient}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </AnimatedSection>
              </div>

              {/* Columna de elaboración */}
              <div className="lg:col-span-2">
                <AnimatedSection animation="fadeUp" delay={0.2}>
                  <h2 className="font-baskerville font-bold text-intercasa-dark text-2xl md:text-3xl mb-8">
                    {t('detail.method')}
                  </h2>
                  
                  <ol className="space-y-6">
                    {recipe.instructions && recipe.instructions.map((instruction, index) => (
                      <li key={index} className="flex items-start">
                        <span className="flex-shrink-0 w-10 h-10 bg-intercasa-red text-white rounded-full flex items-center justify-center font-baskerville font-bold mr-4 text-lg">
                          {index + 1}
                        </span>
                        <div className="flex-1 pt-2">
                          <p className="font-lato text-intercasa-dark/80 text-lg leading-relaxed">
                            {instruction}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </AnimatedSection>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 
        =============================================
        3. PRODUCTOS INTERCASA UTILIZADOS
        =============================================
      */}
      {recipe.intercasaProducts && recipe.intercasaProducts.length > 0 && (
        <section className="bg-white py-12 md:py-20">
          <div className="container mx-auto px-4 md:px-8">
            <div className="max-w-4xl mx-auto">
              <AnimatedSection animation="fadeUp">
                <div className="text-center mb-10">
                  <span className="font-lato text-intercasa-red text-sm uppercase tracking-widest mb-3 block">
                    {t('detail.products.label')}
                  </span>
                  <h2 className="font-baskerville font-bold text-intercasa-dark text-2xl md:text-3xl">
                    {t('detail.products.title')}
                  </h2>
                </div>
              </AnimatedSection>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {recipe.intercasaProducts.map((product, index) => {
                  const productImage = getProductImage(product.link);
                  return (
                    <AnimatedSection key={index} animation="fadeUp" delay={0.1 + (index * 0.05)}>
                      <LocalizedLink
                        to={product.link || '/productos'}
                        className="block bg-intercasa-bone rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 group"
                      >
                        {/* Imagen del producto */}
                        <div className="relative aspect-square bg-white p-4 flex items-center justify-center">
                          {productImage ? (
                            <img 
                              src={productImage} 
                              alt={product.name}
                              className="max-w-full max-h-full object-contain group-hover:scale-110 transition-transform duration-500"
                            />
                          ) : (
                            <div className="w-20 h-20 bg-intercasa-bone rounded-full flex items-center justify-center">
                              <svg className="w-10 h-10 text-intercasa-red/30" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                        
                        {/* Info del producto */}
                        <div className="p-4 text-center">
                          <h3 className="font-baskerville font-bold text-intercasa-dark group-hover:text-intercasa-red transition-colors text-lg">
                            {product.name}
                          </h3>
                          <span className="text-center inline-flex items-center text-intercasa-red text-sm font-medium mt-3 group-hover:gap-2 transition-all">
                            {t('detail.products.view')}
                            <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </span>
                        </div>
                      </LocalizedLink>
                    </AnimatedSection>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 
        =============================================
        4. CTA DE CIERRE
        Según estructura.md: "Crea, combina y disfruta con Intercasa"
        =============================================
      */}
      <section className="bg-intercasa-dark py-16 md:py-20">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection animation="fadeUp">
              <h2 className="font-baskerville font-bold text-white text-2xl md:text-3xl lg:text-4xl mb-6">
                <Trans
                  i18nKey="closing.title"
                  ns="recipes"
                  components={{ hl: <span className="text-intercasa-yellow" /> }}
                />
              </h2>
            </AnimatedSection>
            
            <AnimatedSection animation="fadeUp" delay={0.1}>
              <p className="font-lato text-white/70 text-lg mb-8">
                {t('detail.closing.text')}
              </p>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.2}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <LocalizedLink to="/recetas">
                  <MagneticButton variant="secondary" size="lg">
                    {t('detail.closing.moreRecipes')}
                  </MagneticButton>
                </LocalizedLink>
                <LocalizedLink to="/productos">
                  <MagneticButton variant="primary" size="lg">
                    {t('detail.closing.exploreProducts')}
                  </MagneticButton>
                </LocalizedLink>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* 
        =============================================
        5. RECETAS RELACIONADAS
        =============================================
      */}
      {relatedRecipes.length > 0 && (
        <section className="bg-intercasa-bone py-16 md:py-20">
          <div className="container mx-auto px-4 md:px-8">
            <AnimatedSection animation="fadeUp">
              <div className="text-center mb-10">
                <h2 className="font-baskerville font-bold text-intercasa-dark text-2xl md:text-3xl">
                  {t('detail.related.title')}
                </h2>
              </div>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedRecipes.map((relatedRecipe, index) => (
                <AnimatedSection key={relatedRecipe.id} animation="fadeUp" delay={0.1 + (index * 0.05)}>
                  <RelatedRecipeCard {...relatedRecipe} />
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      )}
    </ImmersiveLayout>
  );
};

export default RecipeDetailPageImmersive;
